import React from 'react';
import PropTypes from 'prop-types';
import { Route, Redirect } from 'react-router-dom';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { replace } from 'connected-react-router';
import moment from 'moment';

import {
  TxButton,
  TxButtonMode,
  TxAlert,
  TxLoadingText,
} from 'texkit-ui/components';

import {
  Form,
  FormActions,
  FormActionsItem,
  SummaryCard,
  Trans,
} from '../../../components';

import { LINK_SERVICES_STEPS } from '.';
import { confirmStepUpAndFinish } from '../../../reducers/agency/linkService/linkServiceActions';
import { DpsDlrLockoutModal } from '../../../components/common/modals';

const LinkServiceStepUpReviewConfirm = ({
  link,
  replace,
  confirmStepUpAndFinish,
}) => {
  if (!link || !link.linkData || !link.app) {
    return <Redirect to="/link" />;
  }

  const {
    personalInformation: {
      firstName = '',
      middleName = '',
      lastName = '',
      suffix = '',
    } = {},
    licenseType,
    licenseClass,
    expirationDate,
    renewalEligibility,
    duplicateEligibility,
  } = link.linkData;

  const isIdCard = licenseType === 'ID';

  const formattedExpirationDate = moment(expirationDate).isValid()
    ? moment(expirationDate).format('MM/DD/YYYY')
    : expirationDate;

  const onSubmit = async (_, formikActions) => {
    try {
      // Call confirm and finish action which handles both stepUp and confirm APIs
      await confirmStepUpAndFinish(formikActions, link.app.appSlug);
    } catch {
      // Expected API/business errors are handled by form status and modals.
      // Avoid rethrowing to prevent runtime overlay noise.
    } finally {
      formikActions.setSubmitting(false);
    }
  };

  return (
    <div className="link-services step-up-review-confirm">
      <h2 className="page-header">
        <Trans
          file="LinkService"
          id={
            isIdCard
              ? 'StepUpReviewConfirmIDTitle'
              : 'StepUpReviewConfirmDLTitle'
          }
        />
      </h2>

      <p className="page-body">
        <Trans
          file="LinkService"
          id={
            isIdCard
              ? 'StepUpReviewConfirmIDDescription'
              : 'StepUpReviewConfirmDLDescription'
          }
        />
      </p>

      <Form
        formikConfig={{
          onSubmit,
        }}
        render={({ FormError, formikProps }) => (
          <>
            <SummaryCard className="summary-card">
              <SummaryCard.List
                items={[
                  {
                    label: (
                      <Trans
                        id="StepUpReviewConfirmLegalName"
                        file="LinkService"
                      />
                    ),
                    value: `${firstName} ${middleName} ${lastName} ${suffix}`.trim(),
                  },
                  {
                    label: <Trans id="Type" file="Labels" />,
                    value:
                      licenseType === 'DL' ? (
                        <Trans file="dps" id="dps_Driver_License" />
                      ) : licenseType === 'CDL' ? (
                        <Trans file="dps" id="cdl_Driver_License" />
                      ) : (
                        <Trans file="dps" id="dps_IDCard_Label" />
                      ),
                  },
                  ...((licenseType === 'DL' || licenseType === 'CDL') &&
                  licenseClass
                    ? [
                        {
                          label: <Trans id="Class" file="Labels" />,
                          value: `${licenseClass}`,
                        },
                      ]
                    : []),
                  {
                    label: <Trans id="Expiration_Date" file="Labels" />,
                    value: `${formattedExpirationDate || ''}`,
                  },
                ]}
              />
            </SummaryCard>

            {renewalEligibility === false && duplicateEligibility === false && (
              <TxAlert
                type="warning"
                className="renewal-eligibility-warning"
                heading={
                  <Trans file="dps" id="dpslr_link_service_unavailable_title" />
                }
              >
                <Trans file="dps" id="dpslr_link_service_unavailable_desc" />
              </TxAlert>
            )}

            <FormError />

            <DpsDlrLockoutModal />

            <FormActions>
              <FormActionsItem>
                <TxButton
                  mode={TxButtonMode.Primary}
                  disabled={formikProps.isSubmitting}
                  type="submit"
                >
                  {formikProps.isSubmitting ? (
                    <TxLoadingText>
                      <Trans file="Labels" id="Loading" fallback="Loading" />
                    </TxLoadingText>
                  ) : (
                    <Trans file="Labels" id="LinkToMyAccount" />
                  )}
                </TxButton>
              </FormActionsItem>
              <FormActionsItem>
                <TxButton
                  mode={TxButtonMode.Secondary}
                  onClick={() =>
                    replace(
                      `/link/${LINK_SERVICES_STEPS.ID}/${link.app.appSlug}`
                    )
                  }
                  disabled={formikProps.isSubmitting}
                  type="button"
                >
                  <Trans file="Labels" id="Back" />
                </TxButton>
              </FormActionsItem>
            </FormActions>
          </>
        )}
      />
    </div>
  );
};

LinkServiceStepUpReviewConfirm.propTypes = {
  link: PropTypes.shape({
    app: PropTypes.shape({
      appSlug: PropTypes.string,
    }),
    linkData: PropTypes.shape({
      personalInformation: PropTypes.shape({
        firstName: PropTypes.string,
        middleName: PropTypes.string,
        lastName: PropTypes.string,
        suffix: PropTypes.string,
      }),
      licenseType: PropTypes.string,
      licenseClass: PropTypes.string,
      expirationDate: PropTypes.string,
      renewalEligibility: PropTypes.bool,
      duplicateEligibility: PropTypes.bool,
    }),
  }),
  replace: PropTypes.func.isRequired,
  confirmStepUpAndFinish: PropTypes.func.isRequired,
};

const mapStateToProps = ({ agency }) => ({
  link: agency.link,
});

const mapDispatchToProps = dispatch =>
  bindActionCreators(
    {
      replace,
      confirmStepUpAndFinish,
    },
    dispatch
  );

const ConnectedLinkServiceStepUpReviewConfirm = connect(
  mapStateToProps,
  mapDispatchToProps
)(LinkServiceStepUpReviewConfirm);

const RouteForLinkServiceStepUpReviewConfirm = () => (
  <Route
    path={`/link/${LINK_SERVICES_STEPS.STEP_UP_REVIEW_CONFIRM}/:appSlug`}
    render={props => <ConnectedLinkServiceStepUpReviewConfirm {...props} />}
  />
);

export default RouteForLinkServiceStepUpReviewConfirm;
